import { useNavigate } from '@tanstack/react-router'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { Flex } from '@/components/Flex'
import { Radio, RadioGroup } from '@/components/RadioGroup'
import { RangeType } from '@/routes/usage-statistics.engagement'

type Props = {
  startDate: Date
  endDate: Date
  rangeType: RangeType
}

export default function EngagementRangeSelector({ endDate, startDate, rangeType }: Props) {
  const { t } = useTranslation()

  const navigate = useNavigate({ from: '/usage-statistics/engagement' })

  const fmtDateInput = (d?: Date) => {
    if (!d) return ''
    // Input expects yyyy-mm-dd in local time
    const iso = new Date(d).toISOString()
    return iso.slice(0, 10)
  }

  const setDate = (key: 'startDate' | 'endDate', value: string) => {
    const jsDate = value ? new Date(value) : undefined
    if (!jsDate || isNaN(jsDate.getTime())) return

    navigate({
      search: (prev) => ({ ...prev, [key]: jsDate }),
    })
  }

  const handleRangeChange = (value: RangeType) => {
    if (value === 'custom') {
      navigate({
        search: (prev) => ({ ...prev, startDate, endDate, timeRange: value }),
      })
    } else {
      navigate({
        search: { timeRange: value },
      })
    }
  }

  return (
    <>
      <Flex gap={12} justify="end" align="center" direction="row" style={{ flexWrap: 'wrap' }}>
        {rangeType === 'custom' && (
          <Flex gap={12} align="center" direction="row" style={{ flexWrap: 'nowrap' }}>
            <label style={{ whiteSpace: 'nowrap' }}>
              {t('usageStats.dateStart')}
              <input
                aria-label={t('usageStats.dateStart')}
                type="date"
                value={fmtDateInput(startDate)}
                onChange={(e) => setDate('startDate', e.currentTarget.value)}
                style={{ marginLeft: 8 }}
              />
            </label>
            <label style={{ whiteSpace: 'nowrap' }}>
              {t('usageStats.dateEnd')}
              <input
                aria-label={t('usageStats.dateEnd')}
                type="date"
                value={fmtDateInput(endDate)}
                onChange={(e) => setDate('endDate', e.currentTarget.value)}
                style={{ marginLeft: 8 }}
              />
            </label>
          </Flex>
        )}

        <RadioGroup
          aria-label="Time range selector"
          name="timeRange"
          value={rangeType}
          variation="segmented"
          onChange={handleRangeChange}>
          <Radio value="week">Last 7 days</Radio>
          <Radio value="month">Last 30 days</Radio>
          <Radio value="quarter">Last 90 days</Radio>
          <Radio value="custom">Custom</Radio>
        </RadioGroup>
      </Flex>
    </>
  )
}
